// ============================================================
// AnimCanvas — dependency-free animated GIF89a encoder
// ------------------------------------------------------------
// - Global 256-colour palette built from all frames (popularity on
//   5-bit-per-channel buckets, black & white always included)
// - Standard variable-width LZW (max 12-bit codes)
// - Infinite loop via NETSCAPE2.0 extension
// - Yields to the event loop between frames so the UI stays responsive
// ============================================================

class ByteWriter {
    constructor(initial = 1 << 20) {
        this.buf = new Uint8Array(initial);
        this.len = 0;
    }
    ensure(n) {
        if (this.len + n <= this.buf.length) return;
        let size = this.buf.length * 2;
        while (size < this.len + n) size *= 2;
        const next = new Uint8Array(size);
        next.set(this.buf.subarray(0, this.len));
        this.buf = next;
    }
    byte(b) { this.ensure(1); this.buf[this.len++] = b & 0xff; }
    short(v) { this.byte(v & 0xff); this.byte((v >> 8) & 0xff); }
    str(s) { for (let i = 0; i < s.length; i++) this.byte(s.charCodeAt(i)); }
    bytes(arr, start = 0, end = arr.length) {
        this.ensure(end - start);
        for (let i = start; i < end; i++) this.buf[this.len++] = arr[i];
    }
    result() { return this.buf.slice(0, this.len); }
}

const bucketKey = (r, g, b) => ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);

/** Build a 256-entry palette from a list of ImageData objects. */
export function buildPalette(images) {
    const hist = new Map();
    for (const img of images) {
        const d = img.data;
        const pixelCount = d.length >> 2;
        const step = Math.max(1, Math.floor(pixelCount / 60000));
        for (let p = 0; p < pixelCount; p += step) {
            const i = p << 2;
            const key = bucketKey(d[i], d[i + 1], d[i + 2]);
            hist.set(key, (hist.get(key) || 0) + 1);
        }
    }
    const keys = [...hist.entries()].sort((a, b) => b[1] - a[1]).map(e => e[0]);
    const black = bucketKey(0, 0, 0);
    const white = bucketKey(255, 255, 255);
    const chosen = [];
    const seen = new Set();
    for (const k of [black, white, ...keys]) {
        if (seen.has(k)) continue;
        seen.add(k);
        chosen.push(k);
        if (chosen.length >= 256) break;
    }
    const palette = chosen.map(k => {
        const r = (k >> 10) & 31, g = (k >> 5) & 31, b = k & 31;
        // Pure black/white stay pure; other buckets map to their centre
        const expand = v => (v === 0 ? 0 : v === 31 ? 255 : (v << 3) | 4);
        return [expand(r), expand(g), expand(b)];
    });
    while (palette.length < 256) palette.push([0, 0, 0]);
    return palette;
}

function makeIndexer(palette) {
    const cache = new Int16Array(32768).fill(-1);
    return (r, g, b) => {
        const key = bucketKey(r, g, b);
        const hit = cache[key];
        if (hit !== -1) return hit;
        let best = 0, bestDist = Infinity;
        for (let i = 0; i < palette.length; i++) {
            const p = palette[i];
            const dr = p[0] - r, dg = p[1] - g, db = p[2] - b;
            const dist = dr * dr * 2 + dg * dg * 4 + db * db * 3;
            if (dist < bestDist) { bestDist = dist; best = i; if (dist === 0) break; }
        }
        cache[key] = best;
        return best;
    };
}

function lzwEncode(indices, out) {
    const minCodeSize = 8;
    const clearCode = 1 << minCodeSize;
    const eoiCode = clearCode + 1;
    const blockBuf = new Uint8Array(255);
    let blockLen = 0;
    let cur = 0, curBits = 0;
    let codeSize = minCodeSize + 1;
    let maxCode = (1 << codeSize) - 1;
    let next = eoiCode + 1;
    let dict = new Map();
    let clearFlag = false;

    const flushByte = (b) => {
        blockBuf[blockLen++] = b;
        if (blockLen === 255) { out.byte(255); out.bytes(blockBuf, 0, 255); blockLen = 0; }
    };
    const emit = (code) => {
        cur |= code << curBits;
        curBits += codeSize;
        while (curBits >= 8) { flushByte(cur & 0xff); cur >>>= 8; curBits -= 8; }
        if (clearFlag) {
            codeSize = minCodeSize + 1;
            maxCode = (1 << codeSize) - 1;
            clearFlag = false;
        } else if (next > maxCode && codeSize < 12) {
            codeSize++;
            maxCode = (1 << codeSize) - 1;
        }
    };

    out.byte(minCodeSize);
    emit(clearCode);
    let prefix = indices[0];
    for (let i = 1; i < indices.length; i++) {
        const k = indices[i];
        const key = (prefix << 8) | k;
        const found = dict.get(key);
        if (found !== undefined) { prefix = found; continue; }
        emit(prefix);
        if (next < 4096) {
            dict.set(key, next++);
        } else {
            dict = new Map();
            next = eoiCode + 1;
            clearFlag = true;
            emit(clearCode);
        }
        prefix = k;
    }
    emit(prefix);
    emit(eoiCode);
    if (curBits > 0) flushByte(cur & 0xff);
    if (blockLen > 0) { out.byte(blockLen); out.bytes(blockBuf, 0, blockLen); }
    out.byte(0); // block terminator
}

/**
 * Encode an animated GIF.
 * @param {Object} opts
 * @param {number} opts.width
 * @param {number} opts.height
 * @param {number} opts.frameCount
 * @param {(i:number)=>ImageData} opts.getFrame   - returns ImageData (width x height, opaque)
 * @param {(i:number)=>number} opts.getDelayMs     - display time of frame i in ms
 * @param {boolean} [opts.loop=true]
 * @param {(p:number)=>void} [opts.onProgress]
 * @returns {Promise<Blob>}
 */
export async function encodeGIF({ width, height, frameCount, getFrame, getDelayMs, loop = true, onProgress }) {
    const yieldUI = () => new Promise(r => setTimeout(r, 0));

    // Pass 1: palette from a sample of frames (max 24 evenly spaced)
    const sampleIdx = [];
    const sampleCount = Math.min(frameCount, 24);
    for (let s = 0; s < sampleCount; s++) sampleIdx.push(Math.floor((s * frameCount) / sampleCount));
    const samples = [];
    for (const i of sampleIdx) { samples.push(getFrame(i)); }
    const palette = buildPalette(samples);
    samples.length = 0;
    const indexOf = makeIndexer(palette);
    await yieldUI();

    const out = new ByteWriter();
    out.str('GIF89a');
    out.short(width);
    out.short(height);
    out.byte(0xf7); // global colour table, 8 bits colour res, 256 entries
    out.byte(0);    // background index
    out.byte(0);    // aspect
    for (const [r, g, b] of palette) { out.byte(r); out.byte(g); out.byte(b); }

    if (loop) {
        out.byte(0x21); out.byte(0xff); out.byte(0x0b);
        out.str('NETSCAPE2.0');
        out.byte(0x03); out.byte(0x01); out.short(0); out.byte(0x00);
    }

    const indices = new Uint8Array(width * height);
    let elapsedMs = 0, elapsedCs = 0;
    for (let f = 0; f < frameCount; f++) {
        const img = getFrame(f);
        const d = img.data;
        for (let p = 0, i = 0; p < indices.length; p++, i += 4) {
            indices[p] = indexOf(d[i], d[i + 1], d[i + 2]);
        }
        // Accumulate delays so rounding error doesn't drift (GIF uses 1/100 s)
        elapsedMs += Math.max(10, getDelayMs(f));
        const targetCs = Math.round(elapsedMs / 10);
        const delayCs = Math.max(2, targetCs - elapsedCs);
        elapsedCs += delayCs;

        // Graphic Control Extension
        out.byte(0x21); out.byte(0xf9); out.byte(0x04);
        out.byte(0x04); // disposal: do not dispose, no transparency
        out.short(delayCs);
        out.byte(0); out.byte(0);
        // Image descriptor
        out.byte(0x2c);
        out.short(0); out.short(0);
        out.short(width); out.short(height);
        out.byte(0);
        lzwEncode(indices, out);

        if (onProgress) onProgress((f + 1) / frameCount);
        await yieldUI();
    }
    out.byte(0x3b);
    return new Blob([out.result()], { type: 'image/gif' });
}
